import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components';
import api from '@/utils/api';
import './NetworkFees.scss';

interface NetworkFeeConfig {
  estimatedGasKlyx: number;
  enabled: boolean;
  multiplier: number;
  lastUpdated: Date;
}

const NetworkFeesAdmin: React.FC = () => {
  const [config, setConfig] = useState<NetworkFeeConfig | null>(null);
  const [calculatedGasFee, setCalculatedGasFee] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    estimatedGasKlyx: 11.5,
    enabled: true,
    multiplier: 1.0,
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await api.get('/admin/system/network-fees');
      setConfig(response.data.config);
      setCalculatedGasFee(response.data.calculatedGasFee);
      setFormData({
        estimatedGasKlyx: response.data.config.estimatedGasKlyx,
        enabled: response.data.config.enabled,
        multiplier: response.data.config.multiplier,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load network fee configuration');
      console.error('Error fetching network fee config:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      await api.put('/admin/system/network-fees', formData);

      setSuccess('Network fee configuration updated successfully!');
      await fetchConfig();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update network fee configuration');
      console.error('Error updating network fee config:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    const newEnabled = !formData.enabled;
    setFormData({ ...formData, enabled: newEnabled });

    try {
      setIsSaving(true);
      setError('');
      setSuccess('');

      await api.put('/admin/system/network-fees', { enabled: newEnabled });

      setSuccess(`Network fees ${newEnabled ? 'enabled' : 'disabled'} successfully!`);
      await fetchConfig();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle network fees');
      console.error('Error toggling network fees:', err);
      // Revert on error
      setFormData({ ...formData, enabled: !newEnabled });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="large" message="Loading network fee configuration..." fullHeight />;
  }

  return (
    <div className="network-fees-admin">
      <div className="admin-header">
        <div>
          <h1>Network Fee Configuration</h1>
          <p className="subtitle">Manage gas fee estimates for blockchain transactions</p>
        </div>
        <button
          className={`btn-toggle ${formData.enabled ? 'btn-enabled' : 'btn-disabled'}`}
          onClick={handleToggleEnabled}
          disabled={isSaving}
        >
          {formData.enabled ? '✓ Fees Enabled' : '✗ Fees Disabled'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      {/* Current Configuration Summary */}
      <div className="config-summary">
        <h2>Current Configuration</h2>
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-label">Base Gas Estimate</div>
            <div className="summary-value">{config?.estimatedGasKlyx.toFixed(2)} KLYX</div>
            <div className="summary-note">Base transaction gas cost</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Multiplier</div>
            <div className="summary-value">×{config?.multiplier.toFixed(2)}</div>
            <div className="summary-note">Safety buffer multiplier</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Calculated Gas Fee</div>
            <div className="summary-value calculated">{calculatedGasFee.toFixed(2)} KLYX</div>
            <div className="summary-note">Actual fee charged to users</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Status</div>
            <div className={`summary-value status ${config?.enabled ? 'enabled' : 'disabled'}`}>
              {config?.enabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className="summary-note">
              Last updated: {config?.lastUpdated ? new Date(config.lastUpdated).toLocaleString() : 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="config-form-card">
        <h2>Update Configuration</h2>
        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="estimatedGasKlyx">
                Base Gas Estimate (KLYX)
                <span className="field-description">
                  Estimated gas cost based on blockchain analysis (~5.67 KLV × 2 KDA ratio)
                </span>
              </label>
              <input
                id="estimatedGasKlyx"
                type="number"
                min="0"
                step="0.01"
                value={formData.estimatedGasKlyx}
                onChange={(e) =>
                  setFormData({ ...formData, estimatedGasKlyx: parseFloat(e.target.value) })
                }
                required
                disabled={isSaving}
              />
            </div>

            <div className="form-group">
              <label htmlFor="multiplier">
                Fee Multiplier
                <span className="field-description">
                  Multiplier for safety buffer (1.0 = exact, 1.2 = +20% buffer, 0.9 = -10% discount)
                </span>
              </label>
              <input
                id="multiplier"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={formData.multiplier}
                onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) })}
                required
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="calculated-preview">
            <h3>Preview</h3>
            <p>
              With these settings, users will be charged approximately{' '}
              <strong>{(formData.estimatedGasKlyx * formData.multiplier).toFixed(2)} KLYX</strong> for
              gas fees per transaction.
            </p>
          </div>

          <button type="submit" className="btn-primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </form>
      </div>

      {/* Information Panel */}
      <div className="info-panel">
        <h2>💡 How Network Fees Work</h2>
        <div className="info-content">
          <div className="info-section">
            <h3>Base Gas Estimate</h3>
            <p>
              The base gas estimate is derived from actual blockchain transaction analysis. Smart contract
              calls typically cost around 5.67 KLV in bandwidth fees on the Klever blockchain.
            </p>
          </div>

          <div className="info-section">
            <h3>KDA Fee Pool Ratio</h3>
            <p>
              The platform uses the KDA fee pool to pay transaction fees with KLYX tokens instead of KLV.
              The current ratio is 2:1 (2 KLYX per 1 KLV), which is why the base estimate is 11.5 KLYX
              (5.67 × 2).
            </p>
          </div>

          <div className="info-section">
            <h3>Fee Multiplier</h3>
            <p>
              The multiplier allows you to add a safety buffer or discount to the base estimate:
            </p>
            <ul>
              <li>
                <strong>1.0</strong> - Exact estimate (no buffer)
              </li>
              <li>
                <strong>&gt; 1.0</strong> - Safety buffer (e.g., 1.2 = +20% to account for network
                fluctuations)
              </li>
              <li>
                <strong>&lt; 1.0</strong> - Discount (e.g., 0.9 = -10% to make fees more attractive)
              </li>
            </ul>
          </div>

          <div className="info-section">
            <h3>Disabling Fees</h3>
            <p>
              When network fees are disabled, the gas estimate will show as 0 KLYX in transaction
              preparation. Users will still need to pay actual blockchain gas fees when signing
              transactions, but won't see the estimate in advance.
            </p>
          </div>

          <div className="info-section warning">
            <h3>⚠️ Important Notes</h3>
            <ul>
              <li>Changes take effect immediately for all new transactions</li>
              <li>Setting fees too low may cause transaction failures</li>
              <li>Setting fees too high may discourage user activity</li>
              <li>Monitor actual gas costs regularly and adjust accordingly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkFeesAdmin;
